import React, { useState, useEffect, useMemo } from 'react';
import logo from './assets/bangla_aj_logo.jpg';
import './App.css';
import {Header} from './components/Header';
import {Sidebar} from './components/Sidebar';
import {NewsCard} from './components/Newscard';
import {LoadingSkeleton} from './components/LoadingSkeleton';
import { AlertCircle, Newspaper, Clock } from 'lucide-react';
import { timeAgo } from './utils'; // Utility function to format time
import { useRef } from 'react';

const App = () => {
    const [articles, setArticles] = useState([]);
const [allArticles, setAllArticles] = useState([]);
const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);
const [selectedSource, setSelectedSource] = useState('All');
const [lastUpdated, setLastUpdated] = useState(new Date());
const [offset, setOffset] = useState(0);
const [hasMore, setHasMore] = useState(true);
const [offsetSupported, setOffsetSupported] = useState(true);
const [searchQuery, setSearchQuery] = useState('');
const [searchOpen, setSearchOpen] = useState(false);
const [selectedArticle, setSelectedArticle] = useState(null);
const [showActionButtons, setShowActionButtons] = useState(true);
const [showSidebarModal, setShowSidebarModal] = useState(false);

const PAGE_SIZE = 10;
const sentinelRef = useRef(null);
const fetchingRef = useRef(false);
const scrollTimeoutRef = useRef(null);

// Hide buttons while scrolling, show after scroll stops
useEffect(() => {
    const handleScroll = () => {
        setShowActionButtons(false);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => {
            setShowActionButtons(true);
        }, 1500); // Show buttons 1.5 seconds after scroll stops
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
        window.removeEventListener('scroll', handleScroll);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
}, []);

// Fetch news from API with pagination
const fetchNews = async (reset = false) => {
    if (fetchingRef.current) return; // prevent concurrent fetches
    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    const currentOffset = reset ? 0 : offset;

        const GRAPHQL_ENDPOINT = 'https://api.banglaaj.news/graphql/';
        const queryWithOffset = `
            query LatestNews($limit: Int, $offset: Int) {
                latestNews(limit: $limit, offset: $offset) {
                    id
                    source
                    title
                    url
                    content
                    createdAt
                    timestamp
                    category
                    thumbnailUrl
                }
            }
        `;

        const queryWithoutOffset = `
            query LatestNews($limit: Int) {
                latestNews(limit: $limit) {
                    id
                    source
                    title
                    url
                    content
                    createdAt
                    timestamp
                    category
                    thumbnailUrl
                }
            }
        `;

        const doFetch = (useOffset, limitOverride) => {
            const variables = useOffset
                ? { limit: PAGE_SIZE, offset: currentOffset }
                : { limit: limitOverride || PAGE_SIZE };
            const body = useOffset ? { query: queryWithOffset, variables } : { query: queryWithoutOffset, variables };

            return fetch(GRAPHQL_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            }).then(res => {
                if (!res.ok) throw new Error('Failed to fetch news. Please try again.');
                return res.json().then(json => ({ json, meta: { useOffset, limitUsed: variables.limit } }));
            });
        };

        try {
            // Server doesn't support `offset` argument. Use the no-offset
            // query and request enough items to slice out the current page.
            const page = Math.floor(currentOffset / PAGE_SIZE);
            const need = PAGE_SIZE * (page + 1) + 1; // request one extra to detect more
            const { json, meta } = await doFetch(false, need);

            const items = (json.data && json.data.latestNews) || [];
            // Slice out the current page from the fetched list
            const pageItems = items.slice(currentOffset, currentOffset + PAGE_SIZE);
            const newArticles = pageItems.map(item => ({
                id: item.id,
                source: item.source,
                title: item.title,
                summary: (item.content || item.summary || '').slice(0, 180) + ((item.content || item.summary || '').length > 180 ? '...' : ''),
                url: item.url,
                publishedAt: item.createdAt || item.timestamp || item.publishedAt,
                thumbnailUrl: item.thumbnailUrl || logo || null,
                category: item.category,
            }));

            setAllArticles(prev => (reset ? newArticles : [...prev, ...newArticles.filter(a => !prev.some(p => p.id === a.id))]));
            setArticles(prev => (reset ? newArticles : [...prev, ...newArticles.filter(a => !prev.some(p => p.id === a.id))]));

            setLastUpdated(new Date());
            const overallFetched = items.length;
            const more = overallFetched > currentOffset + PAGE_SIZE;
            setHasMore(Boolean(more));
            setOffset(currentOffset + PAGE_SIZE);
        } catch (err) {
            setError(err.message || String(err));
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
};

// Initial load
useEffect(() => {
    fetchNews(true);
}, []);
// When source changes, if we already have articles for that source show them,
// otherwise reset pagination and fetch fresh. This avoids unnecessary refetches
// and shows cached content instantly.
useEffect(() => {
    const hasLocal = selectedSource === 'All' ? allArticles.length > 0 : allArticles.some(a => a.source === selectedSource);
    if (hasLocal) {
        // Nothing to do: filteredArticles memo will show cached items
        return;
    }
    // No cached items for this source — reset pagination and fetch but do not
    // clear the cached list (clearing caused layout shrink and scroll jumps).
    setOffset(0);
    fetchNews(true);
}, [selectedSource]);


    // Memoize unique sources from all fetched articles
    const sources = useMemo(() => [
        'All',
        ...Array.from(new Set(allArticles.map(article => article.source)))
    ], [allArticles]);

    // Filter articles based on selected source and search query
    const filteredArticles = useMemo(() => {
        let result = allArticles;
        if (selectedSource !== 'All') {
            result = result.filter(article => article.source === selectedSource);
        }
        if (searchQuery && searchQuery.trim().length) {
            const q = searchQuery.trim().toLowerCase();
            result = result.filter(a => (a.title || '').toLowerCase().includes(q) || (a.summary || '').toLowerCase().includes(q));
        }
        return result;
    }, [allArticles, selectedSource, searchQuery]);

    // Infinite-scroll: observe sentinel and auto-load when visible
    useEffect(() => {
        if (!('IntersectionObserver' in window)) return;
        const el = document.getElementById('scroll-sentinel');
        if (!el) return;
        const obs = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !loading && hasMore && selectedSource === 'All') {
                    fetchNews(false);
                }
            });
        }, { rootMargin: '200px' });
        obs.observe(el);
        return () => obs.disconnect();
    }, [loading, hasMore, selectedSource, offsetSupported]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-gray-100 font-sans">
            <Header />
            <main className="container mx-auto px-4 py-6">
                <div className="lg:flex lg:space-x-8">
                    {/* Mobile Filter Button */}
                    <button onClick={() => setShowSidebarModal(true)} className="lg:hidden mb-4 w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-4 py-3 rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-all font-semibold flex items-center justify-center gap-2">
                        🎚️ সংবাদ ফিল্টার করুন
                    </button>

                    {/* Desktop Sidebar */}
                    <div className="hidden lg:block">
                        <Sidebar 
                            sources={sources} 
                            selectedSource={selectedSource}
                            onSelectSource={setSelectedSource}
                            onRefresh={fetchNews}
                        />
                    </div>

                    {/* Mobile Sidebar Modal */}
                    {showSidebarModal && (
                        <div className="lg:hidden fixed inset-0 z-50 flex items-start justify-end">
                            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowSidebarModal(false)}></div>
                            <div className="relative w-full max-w-xs h-screen bg-gradient-to-b from-slate-800 to-slate-900 border-l border-teal-500/10 overflow-y-auto animate-slide-in-right">
                                <div className="p-4 sticky top-0 bg-slate-800/80 backdrop-blur border-b border-gray-700/50">
                                    <div className="flex justify-between items-center">
                                        <h2 className="text-lg font-bold text-white">সংবাদ উৎস</h2>
                                        <button onClick={() => setShowSidebarModal(false)} className="text-gray-400 hover:text-white text-2xl">✕</button>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <Sidebar 
                                        sources={sources} 
                                        selectedSource={selectedSource}
                                        onSelectSource={(source) => {
                                            setSelectedSource(source);
                                            setShowSidebarModal(false);
                                        }}
                                        onRefresh={() => {
                                            fetchNews(true);
                                            setShowSidebarModal(false);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex-1">
                        {error && (
                            <div className="bg-gradient-to-r from-red-500/20 to-red-600/10 border border-red-500/30 text-red-200 p-5 rounded-xl flex items-start mb-6 backdrop-blur-sm">
                                <AlertCircle className="w-6 h-6 mr-4 flex-shrink-0 mt-0.5 text-red-400"/>
                                <div>
                                    <p className="font-semibold">সমস্যা হয়েছে</p>
                                    <p className="text-sm mt-1 text-red-300">{error}</p>
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                            {loading ? (
                                Array.from({ length: 9 }).map((_, i) => <LoadingSkeleton key={i} />)
                            ) : (
                                filteredArticles.map((article, idx) => (
                                    <div key={article.id} style={{animationDelay: `${idx * 50}ms`}} className="fade-in">
                                        <NewsCard article={article} onOpenRead={(a) => setSelectedArticle(a)} />
                                    </div>
                                ))
                            )}
                        </div>
                        {!loading && filteredArticles.length === 0 && (
                            <div className="text-center py-24 col-span-full">
                                <div className="inline-block p-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-teal-500/10">
                                    <Newspaper className="w-16 h-16 mx-auto text-teal-400 mb-4 opacity-50" />
                                    <h2 className="text-2xl font-bold text-white mb-2">কোনো সংবাদ পাওয়া যায়নি</h2>
                                    <p className="text-gray-400 mt-2">অনুগ্রহ করে অন্য একটি উৎস নির্বাচন করুন বা পরে আবার চেষ্টা করুন।</p>
                                </div>
                            </div>
                        )}
                        {/* sentinel for infinite scroll */}
                        <div id="scroll-sentinel" style={{height: 1}} />
                        <div className={`fixed bottom-6 left-6 lg:right-6 lg:left-auto z-40 flex flex-col lg:flex-row items-center gap-3 lg:gap-0 lg:space-x-3 transition-all duration-300 ${showActionButtons ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                            {searchOpen ? (
                                <div className="flex items-center bg-slate-800/95 backdrop-blur-lg rounded-full px-4 py-3 border border-teal-500/30 shadow-lg shadow-teal-500/20 w-full lg:w-auto">
                                    <input
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        className="bg-transparent outline-none text-sm text-white w-full lg:w-48 placeholder-gray-500 font-medium"
                                        placeholder="সংবাদ খুঁজুন..."
                                        autoFocus
                                    />
                                    <button onClick={() => { setSearchOpen(false); }} className="ml-3 text-gray-400 hover:text-white text-sm font-semibold transition-colors flex-shrink-0">বন্ধ</button>
                                </div>
                            ) : null}
                            <div className="flex gap-3 lg:gap-0 lg:space-x-3">
                                <button
                                    onClick={() => setSearchOpen(s => !s)}
                                    className="bg-gradient-to-br from-indigo-600 to-purple-600 text-white p-4 rounded-full shadow-lg hover:shadow-purple-500/50 hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 font-bold flex-shrink-0"
                                    aria-label="Toggle search"
                                    title="খোঁজা"
                                >
                                    🔍
                                </button>
                                <button
                                    onClick={() => fetchNews(true)}
                                    className="bg-gradient-to-br from-teal-600 to-cyan-600 text-white p-4 rounded-full shadow-lg hover:shadow-teal-500/50 hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 font-bold flex-shrink-0"
                                    aria-label="Refresh feed"
                                    title="রিফ্রেশ করুন"
                                >
                                    ↻
                                </button>
                            </div>
                        </div>

                        {/* Reader modal */}
                        {selectedArticle && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedArticle(null)}></div>
                                <div className="relative max-w-3xl w-full max-h-[85vh] overflow-y-auto bg-gradient-to-br from-slate-800 to-slate-900 text-gray-100 rounded-2xl shadow-2xl shadow-teal-500/20 p-8 z-50 border border-teal-500/10">
                                    <div className="flex justify-between items-start mb-6 pb-6 border-b border-gray-700/50">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-3">
                                                <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
                                                <span className="text-sm font-semibold text-teal-400">{selectedArticle.source}</span>
                                            </div>
                                            <h2 className="text-3xl font-bold text-white leading-tight">{selectedArticle.title}</h2>
                                            <p className="text-sm text-gray-400 mt-3 flex items-center space-x-2">
                                                <Clock size={14} />
                                                <span>{timeAgo(selectedArticle.publishedAt)}</span>
                                            </p>
                                        </div>
                                        <button onClick={() => setSelectedArticle(null)} className="text-gray-400 hover:text-white hover:bg-gray-700/50 p-2 rounded-lg transition-all text-xl">✕</button>
                                    </div>
                                    <div className="prose prose-invert max-w-none text-base leading-relaxed text-gray-300" style={{lineHeight: '1.8'}}>
                                        {selectedArticle.content ? (
                                            <div dangerouslySetInnerHTML={{ __html: selectedArticle.content }} />
                                        ) : (
                                            <p>{selectedArticle.summary}</p>
                                        )}
                                    </div>
                                    <div className="mt-8 pt-6 border-t border-gray-700/50 flex justify-end space-x-4">
                                        <button onClick={() => setSelectedArticle(null)} className="px-5 py-2.5 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700/50 transition-all font-semibold">বন্ধ করুন</button>
                                        <a href={selectedArticle.url} target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-6 py-2.5 rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all font-semibold shadow-lg hover:shadow-teal-500/50">সম্পূর্ণ পড়ুন</a>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
            <footer className="text-center py-8 text-sm text-gray-500 border-t border-gray-800/50 mt-12">
                <p className="text-gray-400">সর্বশেষ আপডেট: <span className="text-teal-400 font-semibold">{lastUpdated.toLocaleTimeString('bn-BD')}</span></p>
                <p className="mt-2 text-gray-600">&copy; {new Date().getFullYear()} Bangla আজ. সর্বাধিকার সংরক্ষিত।</p>
            </footer>
        </div>
    );
};

export default App;
